<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <!-- <ModalIdle :is-active="isIdleModal" /> -->
    <component :is="layout">
      <router-view />
    </component>
    <inactivity-warning
      ref="inactivityWarning"
    />
    <scroll-to-top v-if="enableScrollToTop" />
    <portal-target name="favorites-context-menu" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { toastAlert } from '@core/mixins/toast'
import { mapGetters } from 'vuex'
import inactivityManager from '@/utils/inactivityManager'
import InactivityWarning from '@/views/components/modal/InactivityWarning.vue'
import store from '@/store'
import { initialAbility } from '@/libs/acl/config'
import ModalIdle from './views/components/modal/ModalIdle.vue'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  name: 'App',
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    ModalIdle,
    ScrollToTop,
    InactivityWarning,
  },
  mixins: [sweetAlert, toastAlert],
  data() {
    return {
      onLine: navigator.onLine,
      showBackOnline: false,
      isInactive: false,
      timeout: null,
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    isLoggedIn() {
      return this.$store.state.auth.isLoggedIn
    },
    shouldShowInactivityWarning() {
      const route = this.$route
      const isAuthPage = this.isAuthenticationPage(route)
      return this.$store.state.auth.isLoggedIn && !isAuthPage
    },
    // isIdleModal() {
    //   return this.$store.state.idleVue.isIdle && this.$store.state.auth.isLoggedIn
    // },

    ...mapGetters({
      institution: 'auth/getInstitution',
    }),
  },
  watch: {
    onLine(v) {
      if (v) {
        this.showBackOnline = true
        this.timeout = setTimeout(() => {
          this.showBackOnline = false
        }, 1000)
      }
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }
    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']
    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  created() {
    const storedThemeColors = JSON.parse(localStorage.getItem('themeColors'))
    this.applyPrimaryColor(storedThemeColors?.primaryColor)
  },
  mounted() {
    // Ensure theme is applied after DOM is fully loaded
    const storedThemeColors = JSON.parse(localStorage.getItem('themeColors'))
    this.applyPrimaryColor(storedThemeColors?.primaryColor)

    window.addEventListener('online', this.updateOnlineStatus)
    window.addEventListener('offline', this.updateOnlineStatus)
    inactivityManager.onWarning(() => {
      if (this.shouldShowInactivityWarning && this.$refs.inactivityWarning) {
        this.$refs.inactivityWarning.show()
      }
    })

    inactivityManager.onDismissWarning(() => {
      console.log('🚫 App.vue: Dismissing warning modal')
      if (this.$refs.inactivityWarning) {
        this.$refs.inactivityWarning.hide()
      }
    })

    // Logout callback - handles cross-tab logout
    inactivityManager.onLogout(() => {
      // 'Logout triggered - handling across all tabs'
      this.handleGlobalLogout()
    })
  },
  beforeMount() {
    window.addEventListener('load', this.onLoad)
  },
  beforeDestroy() {
    window.removeEventListener('load', this.onLoad)
    window.removeEventListener('online', this.updateOnlineStatus)
    window.removeEventListener('offline', this.updateOnlineStatus)
    clearTimeout(this.timeout)
  },
  methods: {
    applyPrimaryColor(color) {
      if (!color) return

      // Set the CSS variable on :root
      document.documentElement.style.setProperty('--primary', color, 'important')
    },

    onLogout() {
      this.$ability.update(initialAbility)
      store.dispatch('auth/logout')
      store.dispatch('auth/updateMessage', 'You have been logged out due to inactivity.')
      this.$router.push({ name: 'auth-login' })
    },
    isAuthenticationPage(route) {
      const meta = route.meta || {}
      return (
        (meta.layout === 'full' && meta.resource === 'Auth')
        || route.path.includes('/error-')
        || route.path.includes('/miscellaneous/')
        || meta.requiresAuth === false
        || meta.redirectIfLoggedIn === true
      )
    },
    handleGlobalLogout() {
      // This will run in ALL tabs when any tab logs out
      // Set the logout message for display on login page
      this.$store.dispatch('auth/updateMessage', 'You have been logged out due to inactivity.')

      // Clear user session
      this.$store.dispatch('auth/logout')

      // Update abilities
      this.$ability.update(this.$ability.constructor.initialAbility || [])

      // Redirect to login
      this.$router.push('/login').catch(() => {})
    },

    handleStayLoggedIn() {
      console.log('User chose to stay logged in')
    },

    handleInactivityLogout() {
      console.log('Manual logout from warning modal')
    },
    updateOnlineStatus(e) {
      const { type } = e
      this.onLine = type === 'online'
      if (!this.onLine) {
        this.alertError({
          title: 'No internet connection.',
          text: 'Make sure Wi-Fi or cellular data is turned on, then try again.',
          iconHtml: '<svg data-v-cce37b34="" xmlns="http://www.w3.org/2000/svg" width="40px" height="70px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" color="#F26434" class="feather feather-wifi"><path data-v-cce37b34="" d="M5 12.55a11 11 0 0 1 14.08 0"></path><path data-v-cce37b34="" d="M1.42 9a16 16 0 0 1 21.16 0"></path><path data-v-cce37b34="" d="M8.53 16.11a6 6 0 0 1 6.95 0"></path><line data-v-cce37b34="" x1="12" y1="20" x2="12.01" y2="20"></line></svg>',
        })
      }
      if (this.onLine) {
        this.$swal.close()
      }
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
}
</script>

<style>
@import './@core/styles/global.css';

.table-responsive {
  padding-bottom: 120px !important;
  margin-bottom: 0.5rem;
}

.content-body > .card {
  margin-bottom: 0rem !important;
}

.content.app-content {
  padding: calc(1rem + 4.45rem + 1.3rem) 2rem 0 !important;
}

.table th, .table td {
  padding: 0.7rem !important;
}

.swal2-content {
  color: #000 !important;
  font-weight: 700 !important;
}

/* Prevent layout shift when SweetAlert opens */
html.swal2-shown:not(.swal2-no-backdrop):not(.swal2-toast-shown),
body.swal2-shown:not(.swal2-no-backdrop):not(.swal2-toast-shown) {
  overflow-y: scroll !important;
  padding-right: 0 !important;
}

/* Prevent SweetAlert from changing height to auto (causes footer jump on short pages) */
html.swal2-height-auto,
body.swal2-height-auto {
  height: 100% !important;
}

.logo{
  width: 60%;
}
</style>
